import { endpoints, oids } from "@/configs/Settings";
import { HTTPResponseStatus } from "@/utils/enum";
import { useAuth } from "@/utils/useAuth";
import { useAxios } from "@/utils/useAxios";

interface GetServiceRequestByOrganizationParams {
  /** Organization id sem prefixo, ex: 05490544000100 */
  organizationId: string;
  /** Data inicial YYYY-MM-DD (opcional) */
  startDate?: string;
  /** Data final YYYY-MM-DD (opcional) */
  endDate?: string;
  /** Quantidade de registros (default 100) */
  count?: number;
  /** Incluir Encounter vinculado */
  includeEncounter?: boolean;
  /** Incluir Patient no bundle */
  includePatient?: boolean;
  /** Indica se deve retornar apenas o resumo (opcional) */
  summary?: boolean;
}

/**
 * Busca ServiceRequest por organização via encounter.service-provider
 */
export async function GetServiceRequestByOrganization({
  organizationId,
  startDate,
  endDate,
  count = 100,
  includeEncounter,
  includePatient,
  summary,
}: GetServiceRequestByOrganizationParams) {
  let url = `${endpoints.ANALYTICS}/ServiceRequest?encounter.service-provider=Organization/${oids.cnpj}-${organizationId}`;
  if (startDate) url = `${url}&_lastUpdated=ge${startDate}`;
  if (endDate) url = `${url}&_lastUpdated=le${endDate}`;
  if (count) url = `${url}&_count=${count}`;
  if (includeEncounter) url = `${url}&_include=ServiceRequest:encounter`;
  if (includePatient) url = `${url}&_include=ServiceRequest:patient`;
  if (summary) url = `${url}&_summary=count`;

  const subjectId = await useAuth.getSubjectId();
  const orgIdHeader = await useAuth.getOrganizationId();

  const headers = {
    Accept: "application/fhir+json",
    Authorization: await useAuth.getUserToken(),
    "Content-Type": "application/fhir+json",
    ...(subjectId && { "subject-id": subjectId }),
    ...(orgIdHeader && { "organization-id": orgIdHeader }),
  } as Record<string, string>;

  const response = await useAxios
    .get(url, { headers, timeout: 20000 })
    .then((response) => {
      if (response.status !== HTTPResponseStatus.Success) {
        throw new Error(
          `Get ServiceRequest By Organization: ${response.status}`
        );
      }
      return response.data as fhir5.Bundle;
    })
    .catch((error) => {
      console.error("GetServiceRequestByOrganization error:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      throw new Error(`ServiceRequest API Error: ${error.response?.status} - ${error.response?.data?.message || error.message}`);
    });

  return response;
}
